import { api } from './client';

export type ResetPeriod = 'NEVER' | 'YEAR' | 'MONTH';

export interface NumberScheme {
  id: string;
  document_type: string;
  organization_id: string | null;
  prefix: string;
  include_year: boolean;
  /** ч.1367: рік у номері — 2 (26) або 4 (2026) цифри. */
  year_digits: 2 | 4;
  include_month: boolean;
  include_org_code: boolean;
  counter_width: number;
  separator: string;
  reset_period: ResetPeriod;
  per_organization: boolean;
  is_active: boolean;
}

export interface NumberSchemeCreate {
  document_type: string;
  organization_id?: string | null;
  prefix?: string;
  include_year?: boolean;
  year_digits?: 2 | 4;
  include_month?: boolean;
  include_org_code?: boolean;
  counter_width?: number;
  separator?: string;
  reset_period?: ResetPeriod;
  per_organization?: boolean;
  is_active?: boolean;
}

/** ч.1367: як виглядатиме номер за параметрами схеми (лічильник — `value`). Той самий порядок
 *  частин, що й на сервері (`number_service._compose`). */
export function numberExample(
  s: Pick<NumberScheme, 'prefix' | 'include_year' | 'year_digits' | 'include_month'
    | 'include_org_code' | 'counter_width' | 'separator'>,
  opts: { orgCode?: string | null; date?: Date; value?: number } = {},
): string {
  const d = opts.date ?? new Date();
  const year = String(d.getFullYear());
  const parts: string[] = [];
  if (s.prefix.trim()) parts.push(s.prefix.trim());
  if (s.include_org_code && opts.orgCode) parts.push(opts.orgCode);
  if (s.include_year) parts.push(s.year_digits === 2 ? year.slice(-2) : year);
  if (s.include_month) parts.push(String(d.getMonth() + 1).padStart(2, '0'));
  parts.push(String(opts.value ?? 1).padStart(Math.max(1, s.counter_width), '0'));
  return parts.join(s.separator);
}

/** Номер без схеми (ч.1367): «26-00001». */
export const DEFAULT_NUMBER_FORMAT = {
  prefix: '', include_year: true, year_digits: 2 as const, include_month: false,
  include_org_code: false, counter_width: 5, separator: '-',
};

export interface NumberPreview {
  document_type: string;
  organization_id: string | null;
  preview: string;
}

export const numberSchemes = {
  listDocumentTypes: () =>
    api.get<string[]>('/number-schemes/document-types').then((r) => r.data),
  list: (params?: { document_type?: string; organization_id?: string }) =>
    api.get<NumberScheme[]>('/number-schemes', { params }).then((r) => r.data),
  create: (p: NumberSchemeCreate) =>
    api.post<NumberScheme>('/number-schemes', p).then((r) => r.data),
  update: (id: string, p: Partial<NumberSchemeCreate>) =>
    api.patch<NumberScheme>(`/number-schemes/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/number-schemes/${id}`).then(() => undefined),
  preview: (params: { document_type: string; organization_id?: string }) =>
    api.get<NumberPreview>('/number-schemes/preview', { params }).then((r) => r.data),
};
